/**
 * CsintAnalyticsPanel — staff-only real-time CSINT corpus dashboard.
 *
 * Displays aggregate statistics from Elasticsearch: total documents,
 * active sources, storage size, per-source breakdown with breach
 * metadata, and available data categories per source.
 * Styled in full alignment with the RBACStack / Collect&Track theme.
 */

import React, { useEffect, useState, useMemo } from "react";
import {
  getCsintStats,
  type CsintAnalyticsResponse,
  type CsintSourceStats,
} from "@/api/csintAnalytics";
import {
  Database,
  Layers,
  HardDrive,
  RefreshCw,
  ChevronDown,
  Search,
  ExternalLink,
  ShieldAlert,
  Calendar,
  FileText,
  Hash,
  Globe,
  TrendingUp,
  Activity,
  BarChart3,
  Clock,
} from "lucide-react";

// ─── Category badge config ──────────────────────────────────────────

const CATEGORY_META: Record<
  string,
  { emoji: string; label: string; className: string }
> = {
  identity: {
    emoji: "👤",
    label: "Identité",
    className: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  },
  email: {
    emoji: "📧",
    label: "Email",
    className: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
  },
  phone: {
    emoji: "📱",
    label: "Téléphone",
    className: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  },
  address: {
    emoji: "🏠",
    label: "Adresse",
    className: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  },
  bank: {
    emoji: "🏦",
    label: "IBAN",
    className: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  },
  nir: {
    emoji: "🆔",
    label: "NIR",
    className: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20",
  },
  birth: {
    emoji: "📅",
    label: "Naissance",
    className: "bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/20",
  },
  account: {
    emoji: "🔑",
    label: "Compte",
    className: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20",
  },
  ip: {
    emoji: "🌐",
    label: "IP",
    className: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20",
  },
};

function CategoryBadge({ cat }: { cat: string }) {
  const meta = CATEGORY_META[cat] ?? {
    emoji: "📦",
    label: cat,
    className: "bg-muted text-muted-foreground border-border",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[11px] font-medium tracking-tight ${meta.className}`}
    >
      <span>{meta.emoji}</span>
      <span>{meta.label}</span>
    </span>
  );
}

// ─── Source Row Component ───────────────────────────────────────────

function SourceRow({
  source,
  rank,
}: {
  source: CsintSourceStats;
  rank: number;
}) {
  const [expanded, setExpanded] = useState(false);

  const coveragePercent =
    source.breach.declaredRecords != null && source.breach.declaredRecords > 0
      ? Math.min(
          100,
          (source.docs / source.breach.declaredRecords) * 100
        ).toFixed(1)
      : null;

  return (
    <div className="border-b border-border/50 last:border-b-0 transition-colors">
      <div
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-3 py-3 px-3 cursor-pointer hover:bg-muted/50 select-none rounded-xl transition-colors"
      >
        {/* Rank */}
        <span className="w-6 text-center text-xs font-bold text-muted-foreground shrink-0">
          #{rank}
        </span>

        {/* Source info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground truncate">
              {source.breach.name}
            </span>
            {source.breach.countryCode && (
              <span className="text-[10px] font-semibold text-muted-foreground bg-muted border border-border/60 rounded px-1.5 py-0.5 uppercase tracking-wide">
                {source.breach.countryCode}
              </span>
            )}
          </div>
          {source.breach.domain && (
            <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <Globe className="w-3 h-3 opacity-70" />
              {source.breach.domain}
            </span>
          )}
        </div>

        {/* Doc count & Size */}
        <div className="text-right shrink-0">
          <div className="text-sm font-bold text-foreground">
            {source.docs.toLocaleString("fr-FR")}
          </div>
          <div className="text-[11px] text-muted-foreground font-mono">
            {source.sizeHuman}
          </div>
        </div>

        {/* Expand chevron */}
        <div className="p-1 rounded-lg hover:bg-muted text-muted-foreground transition-transform">
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="pb-4 px-4 pt-1 ml-9 mr-2 space-y-3 bg-muted/20 border border-border/40 rounded-xl mb-3">
          {/* Breach metadata chips */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="p-2 rounded-lg bg-card border border-border/50">
              <div className="text-[10px] text-muted-foreground uppercase font-semibold">
                Date de Breach
              </div>
              <div className="font-semibold text-foreground mt-0.5">
                {source.breach.breachDate || "Non renseignée"}
              </div>
            </div>

            <div className="p-2 rounded-lg bg-card border border-border/50">
              <div className="text-[10px] text-muted-foreground uppercase font-semibold">
                Déclarés vs Indexés
              </div>
              <div className="font-semibold text-foreground mt-0.5">
                {source.breach.declaredRecords != null
                  ? source.breach.declaredRecords.toLocaleString("fr-FR")
                  : "N/A"}{" "}
                <span className="text-muted-foreground font-normal">
                  ({source.docs.toLocaleString("fr-FR")})
                </span>
              </div>
            </div>

            <div className="p-2 rounded-lg bg-card border border-border/50">
              <div className="text-[10px] text-muted-foreground uppercase font-semibold">
                Champs Détectés
              </div>
              <div className="font-semibold text-foreground mt-0.5">
                {source.fieldCount} attributs
              </div>
            </div>

            <div className="p-2 rounded-lg bg-card border border-border/50 truncate">
              <div className="text-[10px] text-muted-foreground uppercase font-semibold">
                Index Elasticsearch
              </div>
              <code className="text-[11px] font-mono text-primary truncate block mt-0.5">
                {source.index}
              </code>
            </div>
          </div>

          {/* Coverage bar */}
          {coveragePercent && (
            <div className="space-y-1.5 p-2.5 rounded-lg bg-card border border-border/50">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground font-medium">
                  Taux de Couverture Indexée
                </span>
                <span className="font-bold text-foreground">
                  {coveragePercent}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                  style={{ width: `${coveragePercent}%` }}
                />
              </div>
            </div>
          )}

          {/* Categories */}
          {source.categories.length > 0 && (
            <div className="pt-1">
              <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1.5">
                Catégories de Données Exfiltrées
              </div>
              <div className="flex flex-wrap gap-1.5">
                {source.categories.map((cat) => (
                  <CategoryBadge key={cat} cat={cat} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Growth Chart Component ───────────────────────────────────────────

type ChartMode = "breach" | "ingest";
type MetricType = "docs" | "size";

interface GrowthPoint {
  id: string;
  name: string;
  slug: string;
  dateStr: string;
  timestamp: number;
  docs: number;
  sizeBytes: number;
  cumulativeDocs: number;
  cumulativeSizeBytes: number;
  sizeHuman: string;
  cumulativeSizeHuman: string;
  countryCode: string | null;
}

function formatBytesCompact(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function CsintGrowthChart({ sources }: { sources: CsintSourceStats[] }) {
  const [mode, setMode] = useState<ChartMode>("breach");
  const [metric, setMetric] = useState<MetricType>("docs");
  const [hoveredPoint, setHoveredPoint] = useState<GrowthPoint | null>(null);

  // Build sorted timeline points
  const points = useMemo(() => {
    const list: Array<{
      id: string;
      name: string;
      slug: string;
      dateStr: string;
      timestamp: number;
      docs: number;
      sizeBytes: number;
      countryCode: string | null;
    }> = [];

    for (const s of sources) {
      let rawDate: string | null = null;
      if (mode === "breach") {
        rawDate = s.breach.breachDate;
      } else {
        rawDate = s.createdAt ?? null;
      }

      let timestamp = 0;
      let dateStr = "Date inconnue";
      if (rawDate) {
        const d = new Date(rawDate);
        if (!Number.isNaN(d.getTime())) {
          timestamp = d.getTime();
          dateStr =
            mode === "breach"
              ? d.toLocaleDateString("fr-FR", { year: "numeric", month: "short" })
              : d.toLocaleDateString("fr-FR", { year: "numeric", month: "short", day: "numeric" });
        }
      }

      list.push({
        id: s.slug,
        name: s.breach.name,
        slug: s.slug,
        dateStr,
        timestamp,
        docs: s.docs,
        sizeBytes: s.sizeBytes,
        countryCode: s.breach.countryCode,
      });
    }

    // Sort by timestamp asc (unknown dates first or last; let's put known dates chronological)
    list.sort((a, b) => {
      if (a.timestamp === 0 && b.timestamp !== 0) return 1;
      if (b.timestamp === 0 && a.timestamp !== 0) return -1;
      return a.timestamp - b.timestamp;
    });

    // Compute cumulative
    let runningDocs = 0;
    let runningSize = 0;
    const result: GrowthPoint[] = list.map((item) => {
      runningDocs += item.docs;
      runningSize += item.sizeBytes;
      return {
        ...item,
        cumulativeDocs: runningDocs,
        cumulativeSizeBytes: runningSize,
        sizeHuman: formatBytesCompact(item.sizeBytes),
        cumulativeSizeHuman: formatBytesCompact(runningSize),
      };
    });

    return result;
  }, [sources, mode]);

  if (points.length === 0) return null;

  // Max value for scaling
  const maxVal = Math.max(
    1,
    ...points.map((p) => (metric === "docs" ? p.cumulativeDocs : p.cumulativeSizeBytes))
  );

  // SVG Area & Line coordinates
  const svgWidth = 800;
  const svgHeight = 220;
  const padLeft = 16;
  const padRight = 16;
  const padTop = 20;
  const padBottom = 28;
  const chartW = svgWidth - padLeft - padRight;
  const chartH = svgHeight - padTop - padBottom;

  const coords = points.map((p, i) => {
    const x = padLeft + (points.length > 1 ? (i / (points.length - 1)) * chartW : chartW / 2);
    const val = metric === "docs" ? p.cumulativeDocs : p.cumulativeSizeBytes;
    const y = padTop + chartH - (val / maxVal) * chartH;
    return { x, y, point: p };
  });

  const pathD = coords.reduce((acc, c, i) => {
    if (i === 0) return `M ${c.x.toFixed(1)} ${c.y.toFixed(1)}`;
    const prev = coords[i - 1];
    if (!prev) return acc;
    const cx = (prev.x + c.x) / 2;
    return `${acc} C ${cx.toFixed(1)} ${prev.y.toFixed(1)}, ${cx.toFixed(1)} ${c.y.toFixed(1)}, ${c.x.toFixed(1)} ${c.y.toFixed(1)}`;
  }, "");

  const firstCoord = coords[0];
  const lastCoord = coords[coords.length - 1];
  const areaD = coords.length > 0 && firstCoord && lastCoord
    ? `${pathD} L ${lastCoord.x.toFixed(1)} ${(padTop + chartH).toFixed(1)} L ${firstCoord.x.toFixed(1)} ${(padTop + chartH).toFixed(1)} Z`
    : "";

  const activePoint = hoveredPoint ?? points[points.length - 1];

  return (
    <div className="p-5 rounded-2xl bg-background border border-border space-y-4 shadow-2xs">
      {/* ── Top Bar: Title + Controls ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-border/60">
        <div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-bold text-foreground tracking-tight">
              Progression & Croissance du Corpus
            </h3>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Évolution cumulée du volume de données injectées ({points.length} sources)
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Timeline Mode selector */}
          <div className="flex items-center rounded-xl bg-muted/60 p-0.5 border border-border text-xs">
            <button
              type="button"
              onClick={() => setMode("breach")}
              className={`px-2.5 py-1 rounded-lg font-medium transition-all ${
                mode === "breach"
                  ? "bg-card text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Par date de fuite
            </button>
            <button
              type="button"
              onClick={() => setMode("ingest")}
              className={`px-2.5 py-1 rounded-lg font-medium transition-all ${
                mode === "ingest"
                  ? "bg-card text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Par indexation ES
            </button>
          </div>

          {/* Metric Selector */}
          <div className="flex items-center rounded-xl bg-muted/60 p-0.5 border border-border text-xs">
            <button
              type="button"
              onClick={() => setMetric("docs")}
              className={`px-2.5 py-1 rounded-lg font-medium transition-all ${
                metric === "docs"
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Documents
            </button>
            <button
              type="button"
              onClick={() => setMetric("size")}
              className={`px-2.5 py-1 rounded-lg font-medium transition-all ${
                metric === "size"
                  ? "bg-primary text-primary-foreground font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Stockage
            </button>
          </div>
        </div>
      </div>

      {/* ── Dynamic Status / Hover Preview ── */}
      {activePoint && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl bg-muted/30 border border-border/70 text-xs">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md bg-primary/10 text-primary border border-primary/20 shrink-0">
              {hoveredPoint ? "Survol" : "Total Cumulé Actuel"}
            </span>
            <span className="font-semibold text-foreground truncate">
              {activePoint.name}
            </span>
            {activePoint.countryCode && (
              <span className="text-[10px] font-semibold text-muted-foreground bg-muted border border-border/60 rounded px-1 py-0.2 uppercase">
                {activePoint.countryCode}
              </span>
            )}
            <span className="text-muted-foreground text-[11px] truncate">
              ({activePoint.dateStr})
            </span>
          </div>

          <div className="flex items-center gap-4 text-xs font-mono shrink-0">
            <div>
              <span className="text-muted-foreground font-sans text-[11px] mr-1">
                Ajout:
              </span>
              <span className="font-semibold text-foreground">
                {metric === "docs"
                  ? `+${activePoint.docs.toLocaleString("fr-FR")}`
                  : `+${activePoint.sizeHuman}`}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground font-sans text-[11px] mr-1">
                Cumul:
              </span>
              <span className="font-bold text-primary">
                {metric === "docs"
                  ? activePoint.cumulativeDocs.toLocaleString("fr-FR")
                  : activePoint.cumulativeSizeHuman}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Vector Curve Chart ── */}
      <div className="relative w-full overflow-hidden rounded-xl bg-card/50 border border-border/60 p-2">
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="w-full h-48 sm:h-56 select-none overflow-visible"
        >
          <defs>
            <linearGradient id="curveGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-primary, #3b82f6)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="var(--color-primary, #3b82f6)" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Background Grid Horizontal Lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
            const y = padTop + chartH * (1 - pct);
            const val = maxVal * pct;
            return (
              <g key={pct}>
                <line
                  x1={padLeft}
                  y1={y}
                  x2={svgWidth - padRight}
                  y2={y}
                  stroke="currentColor"
                  strokeOpacity="0.08"
                  strokeDasharray="3 3"
                />
                <text
                  x={padLeft + 4}
                  y={y - 4}
                  fontSize="9"
                  fill="currentColor"
                  className="text-muted-foreground/60 font-mono"
                >
                  {metric === "docs"
                    ? val >= 1_000_000
                      ? `${(val / 1_000_000).toFixed(1)}M`
                      : val >= 1_000
                        ? `${(val / 1_000).toFixed(0)}k`
                        : `${Math.round(val)}`
                    : formatBytesCompact(val)}
                </text>
              </g>
            );
          })}

          {/* Area fill */}
          {areaD && (
            <path
              d={areaD}
              fill="url(#curveGradient)"
            />
          )}

          {/* Curve stroke */}
          {pathD && (
            <path
              d={pathD}
              fill="none"
              stroke="var(--color-primary, #3b82f6)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Data Points */}
          {coords.map((c, i) => {
            const isHovered = hoveredPoint?.id === c.point.id;
            return (
              <g key={c.point.id}>
                {/* Outer halo on hover */}
                {isHovered && (
                  <circle
                    cx={c.x}
                    cy={c.y}
                    r="8"
                    className="fill-primary/20 animate-pulse"
                  />
                )}
                {/* Core dot */}
                <circle
                  cx={c.x}
                  cy={c.y}
                  r={isHovered ? "5" : "3"}
                  className={`transition-all duration-150 cursor-pointer ${
                    isHovered
                      ? "fill-primary stroke-background stroke-2"
                      : "fill-background stroke-primary stroke-2 hover:fill-primary"
                  }`}
                  onMouseEnter={() => setHoveredPoint(c.point)}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
              </g>
            );
          })}
        </svg>

        {/* X-axis date labels */}
        <div className="flex justify-between items-center px-4 pt-1 text-[10px] font-medium text-muted-foreground/80">
          <span>{points[0]?.dateStr}</span>
          {points.length > 2 && (
            <span>{points[Math.floor(points.length / 2)]?.dateStr}</span>
          )}
          <span>{points[points.length - 1]?.dateStr}</span>
        </div>
      </div>

      {/* ── Subtitle / Timeline Insight Bar ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs pt-1">
        <div className="p-2.5 rounded-xl bg-card border border-border/60">
          <div className="text-[10px] text-muted-foreground uppercase font-semibold">
            Plus Grande Base
          </div>
          <div className="font-bold text-foreground truncate mt-0.5">
            {[...sources].sort((a, b) => b.docs - a.docs)[0]?.breach.name ?? "N/A"}
          </div>
          <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
            {[...sources].sort((a, b) => b.docs - a.docs)[0]?.docs.toLocaleString("fr-FR")} docs
          </div>
        </div>

        <div className="p-2.5 rounded-xl bg-card border border-border/60">
          <div className="text-[10px] text-muted-foreground uppercase font-semibold">
            Taille Moyenne / Base
          </div>
          <div className="font-bold text-foreground mt-0.5 font-mono">
            {formatBytesCompact(
              sources.length > 0
                ? sources.reduce((acc, s) => acc + s.sizeBytes, 0) / sources.length
                : 0
            )}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Moyenne sur l'ensemble
          </div>
        </div>

        <div className="p-2.5 rounded-xl bg-card border border-border/60">
          <div className="text-[10px] text-muted-foreground uppercase font-semibold">
            Mode Actif
          </div>
          <div className="font-bold text-foreground mt-0.5">
            {mode === "breach" ? "Chronologie Fuites" : "Chronologie Indexation"}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {mode === "breach" ? "Selon dates d'exfiltration" : "Selon création ES"}
          </div>
        </div>

        <div className="p-2.5 rounded-xl bg-card border border-border/60">
          <div className="text-[10px] text-muted-foreground uppercase font-semibold">
            Total Enregistré
          </div>
          <div className="font-bold text-primary mt-0.5 font-mono">
            {metric === "docs"
              ? points[points.length - 1]?.cumulativeDocs.toLocaleString("fr-FR")
              : points[points.length - 1]?.cumulativeSizeHuman}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {metric === "docs" ? "Documents finaux" : "Espace occupé"}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

export function CsintAnalyticsPanel(): JSX.Element {
  const [data, setData] = useState<CsintAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  async function fetchStats() {
    setLoading(true);
    setError(null);
    try {
      const result = await getCsintStats();
      setData(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchStats();
  }, []);

  const filteredSources = useMemo(() => {
    if (!data?.sources) return [];
    if (!searchQuery.trim()) return data.sources;
    const q = searchQuery.toLowerCase();
    return data.sources.filter(
      (s) =>
        s.breach.name.toLowerCase().includes(q) ||
        (s.breach.domain && s.breach.domain.toLowerCase().includes(q)) ||
        (s.breach.countryCode && s.breach.countryCode.toLowerCase().includes(q)) ||
        s.categories.some((c) => c.toLowerCase().includes(q))
    );
  }, [data?.sources, searchQuery]);

  if (loading && !data) {
    return (
      <div className="bg-card border border-border rounded-2xl p-8 shadow-xs">
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          <span className="text-xs font-medium text-muted-foreground">
            Interrogation du cluster Elasticsearch en direct…
          </span>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-card border border-border rounded-2xl p-8 shadow-xs">
        <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
          <div className="p-3 rounded-2xl bg-destructive/10 text-destructive">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <p className="text-sm font-semibold text-foreground">
            Impossible d'accéder au cluster CSINT
          </p>
          <p className="text-xs text-muted-foreground max-w-md">{error}</p>
          <button
            type="button"
            onClick={() => void fetchStats()}
            className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (!data) return <></>;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 shadow-xs space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border/60">
        <div className="flex items-start gap-3.5">
          <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0 mt-0.5">
            <Database className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-foreground tracking-tight">
                Analytics CSINT
              </h2>
              <span className="text-[10px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                Cluster Live
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Vue en temps réel du corpus de données breach indexé dans Elasticsearch
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          {data.fetchedAt && (
            <span className="text-[11px] text-muted-foreground hidden md:inline-block">
              Mis à jour : {new Date(data.fetchedAt).toLocaleTimeString("fr-FR")}
            </span>
          )}
          <button
            type="button"
            onClick={() => void fetchStats()}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border bg-card border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
            />
            {loading ? "Actualisation…" : "Actualiser"}
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Total Documents */}
        <div className="p-4 rounded-xl bg-background border border-border shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Total Documents
            </span>
            <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500">
              <Database className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-bold text-foreground tracking-tight">
              {data.total.documents.toLocaleString("fr-FR")}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Entrées breach indexées
            </p>
          </div>
        </div>

        {/* Active Sources */}
        <div className="p-4 rounded-xl bg-background border border-border shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Sources Actives
            </span>
            <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-bold text-foreground tracking-tight">
              {data.total.sources}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Bases & leaks catalogués
            </p>
          </div>
        </div>

        {/* Total Storage Size */}
        <div className="p-4 rounded-xl bg-background border border-border shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Taille Totale
            </span>
            <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-500">
              <HardDrive className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tracking-tight font-mono">
              {data.total.sizeHuman}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Espace disque shardé
            </p>
          </div>
        </div>
      </div>

      {/* ── Growth Chart ── */}
      <CsintGrowthChart sources={data.sources} />

      {/* ── Global Categories Breakdown ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground tracking-tight">
            Types de Données Disponibles
          </h3>
          <span className="text-xs text-muted-foreground">
            {Object.keys(CATEGORY_META).length} types surveillés
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.keys(CATEGORY_META).map((cat) => {
            const matchingSources = data.sources.filter((s) =>
              s.categories.includes(cat)
            );
            const count = matchingSources.length;
            const docsCount = matchingSources.reduce((sum, s) => sum + s.docs, 0);

            if (count === 0) return null;

            const percent =
              data.total.documents > 0
                ? ((docsCount / data.total.documents) * 100).toFixed(1)
                : "0.0";

            return (
              <div
                key={cat}
                className="flex flex-col justify-between p-3.5 rounded-xl bg-background border border-border/70 hover:border-border transition-colors gap-2"
              >
                <div className="flex items-center justify-between">
                  <CategoryBadge cat={cat} />
                  <span className="text-xs font-bold text-foreground">
                    {percent}%
                  </span>
                </div>

                <div className="flex items-baseline justify-between text-xs text-muted-foreground mt-1">
                  <span className="font-semibold text-foreground">
                    {docsCount.toLocaleString("fr-FR")} docs
                  </span>
                  <span className="text-[11px] opacity-80">
                    {count} source{count > 1 ? "s" : ""}
                  </span>
                </div>

                <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden mt-0.5">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${percent}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Sources List ── */}
      <div className="space-y-3 pt-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground tracking-tight">
              Sources Répertoriées ({data.sources.length})
            </h3>
            <p className="text-xs text-muted-foreground">
              Détail par brèche et volumétrie d'indexation
            </p>
          </div>

          {/* Search bar */}
          <div className="relative w-full sm:w-64">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Filtrer une source, pays, tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        {/* Table container */}
        <div className="rounded-xl border border-border bg-background overflow-hidden">
          {/* Table Header */}
          <div className="flex items-center gap-3 py-2.5 px-3 border-b border-border bg-muted/30 text-[11px] uppercase font-bold tracking-wider text-muted-foreground">
            <span className="w-6 text-center">#</span>
            <span className="flex-1">Source & Domaine</span>
            <span className="text-right w-28">Documents</span>
            <span className="w-6" />
          </div>

          {/* Table Rows */}
          <div className="divide-y divide-border/40">
            {filteredSources.length === 0 ? (
              <div className="py-8 text-center text-xs text-muted-foreground">
                Aucune source ne correspond à votre filtre "{searchQuery}".
              </div>
            ) : (
              filteredSources.map((source, i) => (
                <SourceRow
                  key={source.slug}
                  source={source}
                  rank={i + 1}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CsintAnalyticsPanel;
